import { SessionExpiredError } from '@/api/client'
import { getActivation } from '@/api/endpoints/activation.api'
import type { CandidateActivation } from '@/api/types/generated'
import { createSignal } from 'solid-js'

interface ActivationState {
  isLoaded: boolean
  data: CandidateActivation | null
  locked: boolean
}

function freshState(): ActivationState {
  return { isLoaded: false, data: null, locked: true }
}

const [state, setState] = createSignal<ActivationState>(freshState())

async function load(): Promise<void> {
  try {
    const data = await getActivation()
    setState({ isLoaded: true, data, locked: data.state === 'locked' })
    return
  } catch (err) {
    if (err instanceof SessionExpiredError) {
      setState({ isLoaded: true, data: null, locked: false })
      return
    }
    console.warn('[activationStore] first fetch failed, retrying', err)
  }

  try {
    const data = await getActivation()
    setState({ isLoaded: true, data, locked: data.state === 'locked' })
  } catch (err) {
    console.warn('[activationStore] second fetch failed, failing open', err)
    setState({ isLoaded: true, data: null, locked: false })
  }
}

let inFlight: Promise<void> | null = null

function run(): Promise<void> {
  if (inFlight) {
    return inFlight
  }
  inFlight = load().finally(() => {
    inFlight = null
  })
  return inFlight
}

const DISCOVERY_ESCAPE_KEY = 'peatch-discovery-escape'
const JOB_SOUGHT_ESCAPE_KEY = 'peatch-jobsought-escape'

function readEscape(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function persistEscape(key: string): boolean {
  try {
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    return false
  }
}

function clearEscape(key: string): boolean {
  try {
    sessionStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

const [discoveryEscaped, setDiscoveryEscaped] = createSignal(readEscape(DISCOVERY_ESCAPE_KEY))
const [jobSoughtEscaped, setJobSoughtEscaped] = createSignal(readEscape(JOB_SOUGHT_ESCAPE_KEY))

export const activationStore = {
  getState: state,

  get state() {
    return state()
  },

  isLoaded: () => state().isLoaded,
  isLocked: () => state().locked,
  unlockReason: () => state().data?.unlock_reason ?? '',
  nextActions: () => state().data?.next_actions ?? [],
  diagnosis: () => state().data?.diagnosis ?? null,
  moment: () => state().data?.moment ?? null,
  signedUpAt: () => state().data?.signed_up_at ?? null,
  discoveryRequired: () => state().data?.discovery_required ?? false,
  discoveryDone: () => state().data?.discovery_done ?? false,
  focus: () => state().data?.focus ?? 'job_search',
  focusAnswered: () => state().data?.focus_answered ?? false,
  focusRequired: () => state().data?.focus_required ?? false,
  hasDesiredRole: () => state().data?.profile?.has_desired_role ?? false,
  needsFocus: () => activationStore.isLoaded() && !activationStore.focusAnswered(),
  needsDiscovery: () =>
    activationStore.discoveryRequired() && !activationStore.discoveryDone() && !discoveryEscaped(),
  needsJobSoughtForm: () =>
    activationStore.isLoaded() && !activationStore.hasDesiredRole() && !jobSoughtEscaped(),

  escapeDiscovery() {
    setDiscoveryEscaped(true)
    persistEscape(DISCOVERY_ESCAPE_KEY)
  },

  escapeJobSoughtForm() {
    setJobSoughtEscaped(true)
    persistEscape(JOB_SOUGHT_ESCAPE_KEY)
  },

  async fetch(): Promise<void> {
    if (state().isLoaded) {
      return
    }
    await run()
  },

  async refresh(): Promise<void> {
    inFlight = null
    await run()
  },

  reset() {
    inFlight = null
    setState(freshState())
    setDiscoveryEscaped(false)
    setJobSoughtEscaped(false)
    clearEscape(DISCOVERY_ESCAPE_KEY)
    clearEscape(JOB_SOUGHT_ESCAPE_KEY)
  },

  _setForTest(partial: Partial<ActivationState>) {
    setState((s) => ({ ...s, ...partial }))
  },
}
